'use client';

import { useMemo, useState } from 'react';
import type { UfdrArtifactView, UfdrDockPanel, UfdrQuery, UfdrSection } from '@/shared/ufdr/legacy-types';
import type {
  MobileFrontEndBubbleMessageView,
  MobileFrontEndCardView,
  MobileFrontEndGalleryView,
  MobileFrontEndMapView,
  MobileFrontEndTimelineView,
  MobileUiConfigArtifact,
  MobileUiConfigResponse,
} from '@/shared/mobile-service/api/cors-mobile-api';
import { useArtifactFacets } from '@/shared/mobile-service/hooks/useArtifactFacets';
import { useArtifactRecord } from '@/shared/mobile-service/hooks/useArtifactRecord';
import { useCardItems } from '@/shared/mobile-service/hooks/useCardItems';
import { useGalleryItems } from '@/shared/mobile-service/hooks/useGalleryItems';
import { useGlobalSearch } from '@/shared/mobile-service/hooks/useGlobalSearch';
import { useMapPoints } from '@/shared/mobile-service/hooks/useMapPoints';
import { useMessageThreads } from '@/shared/mobile-service/hooks/useMessageThreads';
import { useTimelineEvents } from '@/shared/mobile-service/hooks/useTimelineEvents';
import { resolveArtifactColumns } from '@/shared/mobile-service/lib/columns';
import {
  artifactBubbleView,
  artifactCardView,
  artifactGalleryView,
  artifactMapView,
  artifactTimelineView,
} from '@/shared/mobile-service/lib/labels';
import { filtersFromBrowseParam } from '@/shared/mobile-service/lib/url-state';
import { filterableFieldAllowlist, type FilterState } from '@/shared/mobile-service/lib/where';
import {
  mapArtifactRecord,
  mapCardItems,
  mapFacetResult,
  mapGalleryItems,
  mapMobileTimeline,
  mapPointsToMapData,
  mapResolvedColumns,
  mapSearchGroups,
  mapThreadsToChats,
  splitUfdrChatId,
  ufdrChatId,
} from '@/shared/ufdr-mobile-adapter';

const IDLE_ARTIFACT = {
  graphql_type: 'Idle',
  endpoint: 'mobile',
  fields: [],
} as MobileUiConfigArtifact;

const IDLE_CONFIG = { artifacts: {} } as MobileUiConfigResponse;

const IDLE_MESSAGE_VIEW: MobileFrontEndBubbleMessageView = {
  name: 'messages',
  body_field: 'body',
  timestamp_field: 'timestamp',
  thread: { mode: 'by_field', key_field: 'id' },
};

const IDLE_MAP_VIEW: MobileFrontEndMapView = {
  name: 'map',
  latitude_field: 'latitude',
  longitude_field: 'longitude',
};

const IDLE_GALLERY_VIEW: MobileFrontEndGalleryView = {
  name: 'gallery',
  media_field: 'file',
};

const IDLE_CARD_VIEW: MobileFrontEndCardView = {
  name: 'cards',
  title_field: 'name',
};

const IDLE_TIMELINE_VIEW: MobileFrontEndTimelineView = {
  name: 'timeline',
  timestamp_field: 'timestamp',
};

function toggleEnumFilter(filters: FilterState, field: string, value: string): FilterState {
  const current = filters[field];
  const values = current?.kind === 'enum' ? current.values : [];
  const next = values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
  return { ...filters, [field]: { kind: 'enum', values: next } };
}

/**
 * View-specific mobile-service queries for the artifacts workspace.
 * Each query stays idle unless that view or dock panel is showing.
 */
export function useMobileForensicWorkspace(args: {
  matched: boolean;
  acquisitionId: number | null;
  uiConfig: MobileUiConfigResponse | undefined;
  artifact: MobileUiConfigArtifact | undefined;
  artifactType?: string;
  view?: UfdrArtifactView;
  section: UfdrSection;
  sectionGraphqlType?: string;
  highlightIds?: string[];
  legendLabel: string;
  partialNotice: string;
  openChatId: string | null;
  /** Browse `f` param. Seeded into the facet state so a shared link filters the query. */
  routeFilters?: string;
  /** Browse `q` param. Seeded into the workspace search so a shared link restores it. */
  routeSearch?: string;
  onSearchHit?: (artifactTypeId: string, hitId: string) => void;
}) {
  const acquisitionId = args.acquisitionId ?? 0;
  const showMessages = args.matched && args.view === 'messages';
  const messageView = artifactBubbleView(args.artifact);
  const workspaceMessagesDiffer =
    showMessages &&
    Boolean(args.artifact) &&
    args.artifact?.graphql_type !== args.sectionGraphqlType;
  const workspaceThreads = useMessageThreads({
    mobileAcquisitionId: acquisitionId,
    view: messageView ?? IDLE_MESSAGE_VIEW,
    artifact: args.artifact ?? IDLE_ARTIFACT,
    uiConfig: args.uiConfig ?? IDLE_CONFIG,
    active: workspaceMessagesDiffer,
  });

  const mapView = artifactMapView(args.artifact);
  const mapPoints = useMapPoints({
    mobileAcquisitionId: acquisitionId,
    view: mapView ?? IDLE_MAP_VIEW,
    artifact: args.artifact ?? IDLE_ARTIFACT,
    enabled: args.matched && args.view === 'map' && Boolean(mapView),
  });

  const galleryView = artifactGalleryView(args.artifact);
  const gallery = useGalleryItems({
    mobileAcquisitionId: acquisitionId,
    graphqlType: args.artifact?.graphql_type ?? '',
    artifact: args.artifact ?? IDLE_ARTIFACT,
    view: galleryView ?? IDLE_GALLERY_VIEW,
    offset: 0,
    limit: 60,
    where: null,
    active: args.matched && args.view === 'gallery' && Boolean(galleryView),
  });

  const cardView = artifactCardView(args.artifact);
  const cards = useCardItems({
    mobileAcquisitionId: acquisitionId,
    graphqlType: args.artifact?.graphql_type ?? '',
    artifact: args.artifact ?? IDLE_ARTIFACT,
    uiConfig: args.uiConfig ?? IDLE_CONFIG,
    view: cardView ?? IDLE_CARD_VIEW,
    offset: 0,
    limit: 48,
    where: null,
    active: args.matched && args.view === 'cards' && Boolean(cardView),
  });

  const timelineView = artifactTimelineView(args.artifact);
  const artifactTimeline = useTimelineEvents({
    mobileAcquisitionId: acquisitionId,
    uiConfig: args.uiConfig,
    single:
      args.artifact && timelineView && args.artifactType
        ? {
            graphqlType: args.artifactType,
            typeLabel: args.artifact.title || args.artifactType,
            artifact: args.artifact,
            view: timelineView,
          }
        : {
            graphqlType: 'Idle',
            typeLabel: 'Idle',
            artifact: IDLE_ARTIFACT,
            view: IDLE_TIMELINE_VIEW,
          },
    direction: 'desc',
    offset: 0,
    limit: 200,
    where: null,
    active: args.matched && args.view === 'timeline' && Boolean(timelineView && args.artifact),
  });

  const routeKey = args.routeFilters ?? '';
  const [appliedRouteFilters, setAppliedRouteFilters] = useState(routeKey);
  const [filters, setFilters] = useState<FilterState>(() => filtersFromBrowseParam(args.routeFilters));
  if (routeKey !== appliedRouteFilters) {
    setAppliedRouteFilters(routeKey);
    setFilters(filtersFromBrowseParam(args.routeFilters));
  }
  const [dockPanel, setDockPanel] = useState<UfdrDockPanel>('assistant');
  const [recordId, setRecordId] = useState<string | null>(null);
  const routeSearch = args.routeSearch ?? '';
  const [appliedRouteSearch, setAppliedRouteSearch] = useState(routeSearch);
  const [searchText, setSearchText] = useState(routeSearch);
  if (routeSearch !== appliedRouteSearch) {
    setAppliedRouteSearch(routeSearch);
    setSearchText(routeSearch);
  }

  const facets = useArtifactFacets({
    mobileAcquisitionId: acquisitionId,
    graphqlType: args.artifactType ?? '',
    uiConfig: args.uiConfig,
    filters,
    enabled: args.matched && dockPanel === 'facets' && Boolean(args.artifactType),
  });
  const record = useArtifactRecord({
    mobileAcquisitionId: acquisitionId,
    graphqlType: args.artifactType ?? '',
    recordId: args.matched ? recordId : null,
    uiConfig: args.uiConfig,
  });
  const search = useGlobalSearch({
    mobileAcquisitionId: acquisitionId,
    term: searchText,
    uiConfig: args.uiConfig,
    active: args.matched && args.section === 'artifacts',
  });

  const workspaceChats = useMemo(() => {
    if (!workspaceMessagesDiffer || !args.artifact || !messageView) return undefined;
    return mapThreadsToChats(
      workspaceThreads.data ?? [],
      args.artifact.title || args.artifact.graphql_type,
      args.artifact.graphql_type,
    );
  }, [workspaceMessagesDiffer, args.artifact, messageView, workspaceThreads.data]);

  const resolved = args.artifactType && args.uiConfig
    ? resolveArtifactColumns(args.artifactType, args.uiConfig)
    : null;
  const open = splitUfdrChatId(args.openChatId);
  const recordModel = mapArtifactRecord({
    id: recordId ?? '',
    row: record.data,
    columns: resolved?.columns ?? [],
    title: args.artifact?.title || args.artifactType,
  });

  return {
    filters,
    dockPanel,
    onDockPanelChange: setDockPanel,
    workspaceChats,
    workspaceChatId:
      workspaceMessagesDiffer && open && open.graphqlType === args.artifact?.graphql_type
        ? ufdrChatId(open.graphqlType, open.threadKey)
        : null,
    mapData: args.view === 'map' ? mapPointsToMapData(mapPoints.points, args.legendLabel) : undefined,
    mapPartialNotice: mapPoints.truncated ? args.partialNotice : undefined,
    highlightMarkerIds: args.highlightIds,
    gallery: args.view === 'gallery' ? mapGalleryItems(gallery.data?.items ?? []) : undefined,
    cards: args.view === 'cards' ? mapCardItems(cards.data?.items ?? []) : undefined,
    timeline: args.view === 'timeline' ? mapMobileTimeline(artifactTimeline.data?.events ?? []) : undefined,
    timelineHasMore: Boolean(
      artifactTimeline.data &&
        artifactTimeline.data.events.length < artifactTimeline.data.total &&
        !artifactTimeline.data.truncated,
    ),
    facets: mapFacetResult(facets.query.data, filters),
    dateExtent:
      facets.query.data?.range?.min && facets.query.data.range.max
        ? { from: facets.query.data.range.min, to: facets.query.data.range.max }
        : undefined,
    onFacetToggle: (facetId: string, value: string) => {
      setFilters((current) => toggleEnumFilter(current, facetId, value));
    },
    onDateRangeChange: (range: { from?: string; to?: string }) => {
      const field = facets.query.data?.range?.field;
      if (!field) return;
      setFilters((current) => ({
        ...current,
        [field]: { kind: 'datetime', gte: range.from, lte: range.to },
      }));
    },
    dateRange: (() => {
      const field = facets.query.data?.range?.field;
      const value = field ? filters[field] : undefined;
      if (!value || value.kind !== 'datetime') return undefined;
      return { from: value.gte, to: value.lte };
    })(),
    query: searchText,
    onQueryChangeText: setSearchText,
    searchResults: mapSearchGroups(search.data?.groups),
    onSearchHit: (artifactTypeId: string, hitId: string) => {
      setRecordId(hitId);
      setDockPanel('record');
      args.onSearchHit?.(artifactTypeId, hitId);
    },
    record: recordModel,
    onRowSelect: (id: string) => {
      setRecordId(id);
      setDockPanel('record');
    },
    applyQueryFilters: (query: UfdrQuery) => {
      setFilters((current) => mergeQueryFilters(current, query));
    },
    columns: resolved ? mapResolvedColumns(resolved.columns) : undefined,
    allowlist: filterableFieldAllowlist(args.artifact?.capabilities?.filterable_fields),
    loading:
      (workspaceMessagesDiffer && workspaceThreads.isLoading) ||
      (args.view === 'map' && mapPoints.isLoading) ||
      (args.view === 'gallery' && gallery.isLoading) ||
      (args.view === 'cards' && cards.isLoading) ||
      (args.view === 'timeline' && artifactTimeline.isLoading),
  };
}

export type UfdrWorkspaceModel = ReturnType<typeof useMobileForensicWorkspace>;

/** Keep the table query's string filters in the facet state without dropping enum chips. */
export function mergeQueryFilters(filters: FilterState, query: UfdrQuery): FilterState {
  const next: FilterState = {};
  for (const [field, value] of Object.entries(filters)) {
    if (value.kind !== 'string') next[field] = value;
  }
  for (const [field, value] of Object.entries(query.filters ?? {})) {
    if (value.trim()) next[field] = { kind: 'string', op: 'contains', value };
  }
  return next;
}
